import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Copy, Download, ExternalLink, Link2, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { copyCode } from "@/lib/clipboard";
import { StatusBadge } from "@/components/admin/AdminLayout";
import { PostActionDialog } from "@/components/admin/smm/PostActionDialog";
import { PLATFORM_LABELS, POST_STATUS_LABELS, addDays, istDay, istTime, media, useSmmConnections } from "@/lib/smm";

const FILTERS = ["all", "scheduled", "due", "published", "failed", "cancelled"];
const link = "text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1";

const PostRow = ({ p, onAction, onCancel, onPublish, auto, busy }) => (
  <li className="flex flex-wrap items-center gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`smm-post-${p.id}`}>
    <img src={media(p.creative_url)} alt="" loading="lazy" className="h-12 w-12 rounded-lg object-cover bg-slate-100 shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold line-clamp-1">{p.title}</p>
      <p className="text-xs text-slate-500">{istTime(p.scheduled_at)} IST · {PLATFORM_LABELS[p.platform]}
        <span className={auto ? "text-emerald-700" : "text-amber-700"} data-testid={`smm-post-${p.id}-mode`}> · {auto ? "posts automatically" : "post by hand (reminder)"}</span>
        {p.error && ["failed", "scheduled"].includes(p.status) && <span className="text-red-600" data-testid={`smm-post-${p.id}-error`}> · {p.error}</span>}</p>
    </div>
    <StatusBadge s={p.status} label={POST_STATUS_LABELS[p.status]} />
    <div className="flex flex-wrap gap-3">
      {auto && ["scheduled", "due", "failed"].includes(p.status) && (
        <button className={`${link} !text-emerald-700`} disabled={busy} onClick={() => onPublish(p)} data-testid={`smm-post-${p.id}-publish`}>
          <Send size={13} /> {busy ? "Posting…" : p.status === "failed" ? "Retry now" : "Post now"}
        </button>
      )}
      <button className={link} onClick={() => copyCode(p.caption, "Caption copied")} data-testid={`smm-post-${p.id}-copy`}><Copy size={13} /> Copy</button>
      <button className={link} onClick={() => copyCode(p.tracked_url, "Tracked link copied")} data-testid={`smm-post-${p.id}-copy-link`}><Link2 size={13} /> Link</button>
      <a className={link} href={media(p.creative_url)} download target="_blank" rel="noreferrer" data-testid={`smm-post-${p.id}-download`}><Download size={13} /> Design</a>
      {p.post_url && p.status === "published" && <a className={link} href={p.post_url} target="_blank" rel="noreferrer" data-testid={`smm-post-${p.id}-live`}><ExternalLink size={13} /> Live post</a>}
      {["scheduled", "due", "failed"].includes(p.status) && <button className={link} onClick={() => onAction(p, "edit")} data-testid={`smm-post-${p.id}-edit`}>Edit</button>}
      {["scheduled", "due", "failed"].includes(p.status) && <button className={`${link} !text-emerald-700`} onClick={() => onAction(p, "published")} data-testid={`smm-post-${p.id}-mark-published`}>Mark published</button>}
      {["scheduled", "due"].includes(p.status) && <button className={link} onClick={() => onAction(p, "failed")} data-testid={`smm-post-${p.id}-mark-failed`}>Mark failed</button>}
      {["scheduled", "due", "failed"].includes(p.status) && <button className={`${link} !text-red-600`} onClick={() => onCancel(p)} data-testid={`smm-post-${p.id}-cancel`}>Cancel</button>}
    </div>
  </li>
);

export const SmmCalendar = () => {
  const qc = useQueryClient();
  const [start, setStart] = useState(istDay());
  const [filter, setFilter] = useState("all");
  const [action, setAction] = useState(null);
  const end = addDays(start, 6);
  const { data, isLoading } = useQuery({ queryKey: ["smm-posts", start], queryFn: () => api.get(`/admin/smm/posts?start=${start}&end=${end}`).then((r) => r.data), refetchInterval: 60000 });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["smm-posts"] }); qc.invalidateQueries({ queryKey: ["smm-items"] }); };
  const conn = useSmmConnections();
  const [posting, setPosting] = useState("");
  const publish = async (p) => {
    setPosting(p.id);
    try {
      const { data: r } = await api.post(`/admin/smm/posts/${p.id}/publish`);
      r.status === "published" ? toast.success(`Posted on ${PLATFORM_LABELS[p.platform]}`) : toast.error(`Not posted: ${r.error || r.status}`);
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setPosting(""); }
  };
  const cancel = async (p) => {
    if (!window.confirm("Cancel this scheduled post?")) return;
    try { await api.post(`/admin/smm/posts/${p.id}/cancel`); toast.success("Cancelled"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const posts = (data?.posts || []).filter((p) => filter === "all" || p.status === filter);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="space-y-4" data-testid="smm-calendar">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setStart(addDays(start, -7))} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" aria-label="Previous week" data-testid="smm-cal-prev"><ChevronLeft size={16} /></button>
        <span className="font-semibold text-sm" data-testid="smm-cal-range">{new Date(`${start}T12:00:00+05:30`).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} – {new Date(`${end}T12:00:00+05:30`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} (IST)</span>
        <button onClick={() => setStart(addDays(start, 7))} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" aria-label="Next week" data-testid="smm-cal-next"><ChevronRight size={16} /></button>
        <button onClick={() => setStart(istDay())} className="text-xs font-semibold text-emerald-700" data-testid="smm-cal-today">Today</button>
        <div className="flex flex-wrap gap-1.5 ml-auto">
          {FILTERS.map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`smm-cal-filter-${s}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full ${filter === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
              {s === "all" ? "All" : POST_STATUS_LABELS[s]} {s !== "all" && data?.counts?.[s] ? `(${data.counts[s]})` : ""}
            </button>
          ))}
        </div>
      </div>
      {isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {days.map((d) => {
        const list = posts.filter((p) => istDay(p.scheduled_at) === d);
        return (
          <section key={d} className="bg-white border border-slate-200 rounded-2xl px-4 py-3" data-testid={`smm-cal-day-${d}`}>
            <h3 className="text-sm font-bold text-slate-700">{new Date(`${d}T12:00:00+05:30`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" })}{d === istDay() && <span className="ml-2 text-emerald-700">Today</span>}</h3>
            {list.length ? <ul>{list.map((p) => <PostRow key={p.id} p={p} auto={!!conn[p.platform]} busy={posting === p.id} onPublish={publish} onAction={(post, mode) => setAction({ post, mode })} onCancel={cancel} />)}</ul>
              : <p className="text-xs text-slate-400 py-2">Nothing scheduled.</p>}
          </section>
        );
      })}
      <PostActionDialog post={action?.post} mode={action?.mode} onClose={() => setAction(null)} onDone={() => { setAction(null); refresh(); }} />
    </div>
  );
};
